import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon, Email as EmailIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { authAPI } from '../../services/api';
import UserAvatar from '../common/UserAvatar';
import SidebarNav from './sidebar/SidebarNav';
import ProfileMenu from './account/ProfileMenu';
import { menuItems } from './menu/mainMenu';

const drawerWidth = 280;

const hasActiveDescendant = (item, currentPage) => {
  return (item.children || []).some((child) =>
    child.type === 'group'
      ? (child.children || []).some((leaf) => leaf.id === currentPage)
      : child.id === currentPage
  );
};

const getInitialCollapsedMenus = (currentPage) => {
  return menuItems.reduce((state, item) => {
    if (item.children) {
      state[item.id] = hasActiveDescendant(item, currentPage) || item.id === 'academic';
    }
    return state;
  }, {});
};

const Layout = ({ children, user, onLogout, currentPage, setCurrentPage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [collapsedMenus, setCollapsedMenus] = useState(() => getInitialCollapsedMenus(currentPage));

  const drawerContent = useMemo(
    () => (
      <div>
        <Toolbar sx={{ minHeight: '56px !important', display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src="/Logo.png" alt="School Sync" style={{ height: '32px', width: 'auto' }} />
          <Typography
            variant="h6"
            noWrap
            component="div"
            color="primary"
            sx={{ fontSize: '1rem', fontWeight: 600 }}
          >
            School Sync
          </Typography>
        </Toolbar>
        <SidebarNav
          menuItems={menuItems}
          collapsedMenus={collapsedMenus}
          onToggleMenu={(menuId) =>
            setCollapsedMenus((prev) => ({
              ...prev,
              [menuId]: !prev[menuId],
            }))
          }
          currentPage={currentPage}
          onSelectPage={setCurrentPage}
        />
      </div>
    ),
    [collapsedMenus, currentPage, setCurrentPage],
  );

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
      return;
    }
    setSidebarOpen((prev) => !prev);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    handleProfileMenuClose();
    onLogout();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: {
            xs: '100%',
            md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
          },
          ml: {
            xs: 0,
            md: sidebarOpen ? `${drawerWidth}px` : 0,
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: '1rem' }}>
            All in one solution For Schools
          </Typography>
          {user?.email && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, mr: 2 }}>
              <EmailIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                {user.email}
              </Typography>
            </Box>
          )}
          {user?.phone_number && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, mr: 2 }}>
              <PhoneIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                {user.phone_number}
              </Typography>
            </Box>
          )}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <UserAvatar user={user} size={32} />
          </IconButton>
          <ProfileMenu anchorEl={anchorEl} onClose={handleProfileMenuClose} onLogout={handleLogout} />
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { xs: 0, md: sidebarOpen ? drawerWidth : 0 },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: {
            xs: '100%',
            md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
          },
          ml: {
            xs: 0,
            md: 0,
          },
          mt: { xs: 7, md: 8 },
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;

